import React, { useEffect, useState, useRef, useCallback } from 'react';
import {
  Card, CardContent, Typography, CircularProgress, Grid,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, Box, FormControl, InputLabel, Select, MenuItem, Tabs, Tab, useMediaQuery,
  useTheme, AppBar, Toolbar, IconButton, Stack, TextField, TablePagination, Button,
  LinearProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, startOfMonth, endOfMonth, subDays, subMonths, parseISO, isAfter, isBefore, isEqual , startOfToday} from 'date-fns';
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts';
import { 
  TrendingUp, AccountCircle, CreditCard, FilterList, PieChart as PieChartIcon,
  ShowChart, Assessment, CompareArrows, FileDownload
} from '@mui/icons-material';
import MenuIcon from '@mui/icons-material/Menu';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as XLSX from 'xlsx';
import PageLoader from './pageLoader';

// --- Client-side response cache (survives app switches; sessionStorage on reload) ---
const METRICS_CACHE_STORAGE_KEY = 'subsDash_metricsCache';
const INSTALLS_CACHE_STORAGE_KEY = 'subsDash_installsCache';

const _metricsCache = new Map();
const _installsCache = new Map();

const _readSessionCache = (storageKey) => {
  try {
    const raw = sessionStorage.getItem(storageKey);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const _hydrateCacheFromSession = (map, storageKey) => {
  const stored = _readSessionCache(storageKey);
  Object.entries(stored).forEach(([key, value]) => {
    if (value != null) {
      map.set(key, value);
    }
  });
};

const _persistCacheToSession = (map, storageKey) => {
  try {
    const obj = Object.fromEntries(map.entries());
    sessionStorage.setItem(storageKey, JSON.stringify(obj));
  } catch {
    // sessionStorage quota or private mode — in-memory cache still works
  }
};

_hydrateCacheFromSession(_metricsCache, METRICS_CACHE_STORAGE_KEY);
_hydrateCacheFromSession(_installsCache, INSTALLS_CACHE_STORAGE_KEY);

const COLORS = JSON.parse(process.env.REACT_APP_CHART_COLORS || '["#0088FE", "#00C49F", "#FFBB28", "#FF8042", "#8884d8", "#82ca9d", "#ffc658", "#8dd1e1"]');

const formatLocalDate = (date) => {
  if (!(date instanceof Date) || isNaN(date)) {
    return new Date().toISOString().slice(0, 10);
  }
  const offset = date.getTimezoneOffset();
  const localDate = new Date(date.getTime() - offset * 60 * 1000);
  return localDate.toISOString().slice(0, 10);
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

const SubscriptionDashboard = (props) => {
  const {selectedApp, APP_LIST, setLoading, loading} = props;
  const [tableSubs, setTableSubs] = useState([]);
  const [tableTotal, setTableTotal] = useState(0);
  const [tableLoading, setTableLoading] = useState(false);
  const [productSkus, setProductSkus] = useState([]);
  const [productBreakdown, setProductBreakdown] = useState([]);
  const [filteredPeriodRevenue, setFilteredPeriodRevenue] = useState(0);
  const [filteredTransactionCount, setFilteredTransactionCount] = useState(0);
  
  const getValidDate = (date) => {
    if (!date) return new Date();
    const parsedDate = new Date(date);
    return isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
  };

  const [startDate, setStartDate] = useState(() => {
    const saved = localStorage.getItem(process.env.REACT_APP_STORAGE_START_DATE);
    return getValidDate(saved) || subDays(new Date(), 30);
  });
  
  const [endDate, setEndDate] = useState(() => {
    const saved = localStorage.getItem(process.env.REACT_APP_STORAGE_END_DATE);
    return getValidDate(saved) || new Date();
  });
  
  const [dateRange, setDateRange] = useState(() => localStorage.getItem(process.env.REACT_APP_STORAGE_DATE_RANGE) || '30days');
  const [tabValue, setTabValue] = useState(() => parseInt(localStorage.getItem(process.env.REACT_APP_STORAGE_TAB_VALUE)) || 0);
  const [productFilter, setProductFilter] = useState(() => localStorage.getItem(process.env.REACT_APP_STORAGE_PRODUCT_FILTER) || 'all');
  
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const today = new Date();
  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  
  const [day1, setDay1] = useState(today);
  const [day2, setDay2] = useState(yesterday);
  
  const [day1Metrics, setDay1Metrics] = useState({});
  const [day2Metrics, setDay2Metrics] = useState({});
  const [todaySubs, setTodaySubs] = useState(0);
  const [newTodaySubs, setNewTodaySubs] = useState(0);
  const [renewalTodaySubs, setRenewalTodaySubs] = useState(0);
  const [todayRevenue, setTodayRevenue] = useState(0);
  const [todayCancellations, setTodayCancellations] = useState(0);
  const [todayExpired, setTodayExpired] = useState(0);
  const [activeSubs, setActiveSubs] = useState(0);
  const [todayInstalls, setTodayInstalls] = useState(0);
  const [todayUninstalls, setTodayUninstalls] = useState(0);
  const [todayRefunds, setTodayRefunds] = useState(0);
  const [dailyInstalls, setDailyInstalls] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [dailySubsData, setDailySubsData] = useState([]);
  const [dailyData, setDailyData] = useState([]);

  const metricsRequestId = useRef(0);
  const installsRequestId = useRef(0);
  const tableRequestId = useRef(0);
  const isFirstLoadRef = useRef(true);

  const overlayInstallMetrics = useCallback((rows, installData) => {
    if (!installData) {
      return rows;
    }
    const byDate = {};
    (installData.daily_installs || []).forEach((row) => {
      byDate[row.date] = row;
    });
    return rows.map((row) => ({
      ...row,
      first_open: byDate[row.date]?.first_open || 0,
      app_remove: byDate[row.date]?.app_remove || 0,
    }));
  }, []);

  const applyMetricsData = useCallback((data, installData = null) => {
    setTodaySubs(data.todaySubscriptions || 0);
    setNewTodaySubs(data.newTodaySubscriptions || 0);
    setRenewalTodaySubs(data.renewalTodaySubscriptions || 0);
    setTodayRevenue(data.todayRevenue || 0);
    setActiveSubs(data.activeSubscriptions || 0);
    setTodayCancellations(data.todayCancellations || 0);
    setTodayExpired(data.todayExpired || 0);
    setTodayRefunds(data.todayRefunds || 0);
    setFilteredPeriodRevenue(data.filteredPeriodRevenue || 0);
    setFilteredTransactionCount(data.filteredTransactionCount || 0);
    setProductSkus(Array.isArray(data.productSkus) ? data.productSkus : []);
    setProductBreakdown(Array.isArray(data.productBreakdown) ? data.productBreakdown : []);

    if (installData) {
      setTodayInstalls(installData.todayInstalls || 0);
      setTodayUninstalls(installData.todayUninstalls || 0);
    }

    const expiredLookup = {};
    if (data.daily_expired) {
      data.daily_expired.forEach((item) => {
        expiredLookup[item.date] = item.expiredCount;
      });
    }

    let processedData = (data.daily_metrics || []).map((metric) => ({
      date: metric.date,
      revenue: metric.revenue || 0,
      newCount: metric.newSubscriptions || 0,
      renewalCount: metric.renewalSubscriptions || 0,
      todayCancellations: metric.cancellations || 0,
      expiredCount: expiredLookup[metric.date] || metric.expired || 0,
      first_open: 0,
      app_remove: 0,
    }));
    processedData = overlayInstallMetrics(processedData, installData);

    setDailySubsData(processedData);
    setDailyData(processedData.map((d) => ({ date: d.date, revenue: d.revenue || 0 })));
    setDailyInstalls(
      processedData.map((d) => ({
        date: d.date,
        first_open: d.first_open || 0,
        app_remove: d.app_remove || 0,
      }))
    );
    setLastRefreshed(new Date());
  }, [overlayInstallMetrics]);

  const mergeInstallMetrics = useCallback((installData) => {
    setTodayInstalls(installData.todayInstalls || 0);
    setTodayUninstalls(installData.todayUninstalls || 0);
    setDailySubsData((prev) => overlayInstallMetrics(prev, installData));
    setDailyInstalls(
      (installData.daily_installs || []).map((d) => ({
        date: d.date,
        first_open: d.first_open || 0,
        app_remove: d.app_remove || 0,
      }))
    );
  }, [overlayInstallMetrics]);

  const fetchInstallMetrics = useCallback(async (appId, startStr, endStr, { mergeOnly = false } = {}) => {
    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) return null;

    const cacheKey = `${appId}|${startStr}|${endStr}`;
    const requestId = ++installsRequestId.current;
    const cached = _installsCache.get(cacheKey);

    try {
      let installData = cached;
      if (!installData) {
        const params = new URLSearchParams({
          app_id: appId,
          start_date: startStr,
          end_date: endStr,
        });
        const res = await fetch(`${baseUrl}/dashboard-install-metrics?${params}`);
        installData = await res.json();
        if (requestId !== installsRequestId.current) return null;
        _installsCache.set(cacheKey, installData);
        _persistCacheToSession(_installsCache, INSTALLS_CACHE_STORAGE_KEY);
      }

      if (mergeOnly) {
        mergeInstallMetrics(installData);
      }
      return installData;
    } catch (error) {
      console.error('Error fetching install metrics:', error);
      return null;
    }
  }, [mergeInstallMetrics]);

  const fetchDashboard = useCallback(async (appId, start, end, product) => {
    const validStart = getValidDate(start);
    const validEnd = getValidDate(end);
    if (!appId || !validStart || !validEnd) {
      setLoading(false);
      setRefreshing(false);
      return;
    }

    const baseUrl = process.env.REACT_APP_API_BASE_URL;
    if (!baseUrl) {
      console.warn("Subscription dashboard: REACT_APP_API_BASE_URL is not set.");
      setLoading(false);
      setRefreshing(false);
      return;
    }

    let startStr = formatLocalDate(validStart);
    let endStr = formatLocalDate(validEnd);
    if (startStr > endStr) {
      [startStr, endStr] = [endStr, startStr];
    }
    const productKey = product && product !== 'all' ? product : 'all';
    const cacheKey = `${appId}|${startStr}|${endStr}|${productKey}`;
    const requestId = ++metricsRequestId.current;
    const isInitial = isFirstLoadRef.current;

    const installCacheKey = `${appId}|${startStr}|${endStr}`;
    const cachedInstalls = _installsCache.get(installCacheKey);

    const cached = _metricsCache.get(cacheKey);
    if (cached) {
      applyMetricsData(cached, cachedInstalls || null);
      setLoading(false);
      setRefreshing(true);
    } else if (isInitial) {
      setLoading(true);
    } else {
      setRefreshing(true);
    }

    const params = new URLSearchParams({
      app_id: appId,
      start_date: startStr,
      end_date: endStr,
    });
    if (product && product !== 'all') {
      params.append('product_sku', product);
    }

    try {
      const res = await fetch(`${baseUrl}/dashboard-data?${params}`);
      if (!res.ok) {
        throw new Error(`Failed to load dashboard data: ${res.status}`);
      }
      const data = await res.json();
      if (requestId !== metricsRequestId.current) return;

      _metricsCache.set(cacheKey, data);
      _persistCacheToSession(_metricsCache, METRICS_CACHE_STORAGE_KEY);
      applyMetricsData(data, cachedInstalls || null);

      if (isInitial) {
        isFirstLoadRef.current = false;
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      if (!cached && process.env.REACT_APP_ENABLE_ERROR_ALERTS === 'true') {
        alert('Error fetching data from server');
      }
      if (requestId === metricsRequestId.current && isInitial) {
        isFirstLoadRef.current = false;
        setLoading(false);
      }
    } finally {
      if (requestId === metricsRequestId.current) {
        setRefreshing(false);
      }
    }

    if (requestId === metricsRequestId.current) {
      fetchInstallMetrics(appId, startStr, endStr, { mergeOnly: true });
    }
  }, [applyMetricsData, fetchInstallMetrics, setLoading]);

  useEffect(() => {
    if (day1 && day2) {
      setLoadingCharts(true);
      handleDayComparison();
    }
  }, [day1, day2]);
  
  useEffect(() => {
    const validStartDate = getValidDate(startDate);
    const validEndDate = getValidDate(endDate);
    if (validStartDate) localStorage.setItem(process.env.REACT_APP_STORAGE_START_DATE, validStartDate.toISOString());
    if (validEndDate) localStorage.setItem(process.env.REACT_APP_STORAGE_END_DATE, validEndDate.toISOString());
  }, [startDate, endDate]);
  
  useEffect(() => { localStorage.setItem(process.env.REACT_APP_STORAGE_DATE_RANGE, dateRange); }, [dateRange]);
  useEffect(() => { localStorage.setItem(process.env.REACT_APP_STORAGE_PRODUCT_FILTER, productFilter); }, [productFilter]);
  useEffect(() => { localStorage.setItem(process.env.REACT_APP_STORAGE_TAB_VALUE, tabValue); }, [tabValue]);
  
  useEffect(() => {
    if (!selectedApp || !startDate || !endDate) return;

    const debounceTimer = setTimeout(() => {
      fetchDashboard(selectedApp, startDate, endDate, productFilter);
    }, 100);

    let intervalId;
    if (process.env.REACT_APP_ENABLE_AUTO_REFRESH === 'true') {
      intervalId = setInterval(() => {
        fetchDashboard(selectedApp, startDate, endDate, productFilter);
      }, parseInt(process.env.REACT_APP_AUTO_REFRESH_INTERVAL) || 120000);
    }

    return () => {
      clearTimeout(debounceTimer);
      if (intervalId) clearInterval(intervalId);
    };
  }, [selectedApp, startDate, endDate, productFilter, fetchDashboard]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const handleDateRangeChange = (range) => {
    setDateRange(range);
    let newStartDate;
    const now = new Date();
  
    switch (range) {
      case 'last24':
        newStartDate = new Date(Date.now() - 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case '7days':
        newStartDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case '30days':
        newStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case '90days':
        newStartDate = new Date(Date.now() - 90 * 24 * 60 * 60 * 1000 - 2 * 60 * 60 * 1000);
        break;
      case 'thisMonth':
        newStartDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
        break;
      case 'lastMonth': {
        const firstOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
        const endOfLastMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0, 23, 59, 59));
        newStartDate = firstOfLastMonth;
        setEndDate(endOfLastMonth);
        break;
      }
      default:
        newStartDate = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
    }
  
    setStartDate(newStartDate);
    if (range !== 'lastMonth') {
      setEndDate(new Date()); // current UTC time
    }
    console.log('end date', endDate);
  };  

  // const handleSort = (key) => {
  //   let direction = 'asc';
  //   if (sortConfig.key === key && sortConfig.direction === 'asc') {
  //     direction = 'desc';
  //   }
  //   setSortConfig({ key, direction });
  // };

  // const sortedSubs = React.useMemo(() => {
  //   let sortableItems = [...filteredSubs];
  //   if (sortConfig.key) {
  //     sortableItems.sort((a, b) => {
  //       if (a[sortConfig.key] < b[sortConfig.key]) {
  //         return sortConfig.direction === 'asc' ? -1 : 1;
  //       }
  //       if (a[sortConfig.key] > b[sortConfig.key]) {
  //         return sortConfig.direction === 'asc' ? 1 : -1;
  //       }
  //       return 0;
  //     });
  //   }
  //   return sortableItems;
  // }, [filteredSubs, sortConfig]);

  const chartDailyRevenue = dailyData;
  const productData = productBreakdown;
  const productTypes = productSkus;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const fetchDaysCompare = async (appId, date1, date2) => {
    const params = new URLSearchParams();
    if (appId) params.append('app_id', appId);

    params.append('date1', formatLocalDate(date1));
    params.append('date2', formatLocalDate(date2));

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/compare-revenue?${params.toString()}`);
      const data = await res.json();
      setDay1Metrics(data.date1_metrics);
      setDay2Metrics(data.date2_metrics);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoadingCharts(false);
    }
  };

  const handleDayComparison = () => {
    if (day1 && day2) {
      fetchDaysCompare(selectedApp, day1, day2);
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: 'purchaseDate', direction: 'desc' });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    purchaseDate: '',
    productSku: '',
    convertedPrice: '',
    type: '',
    status: '',
    country: '',
    platform: '',
  });
  const [debouncedTableFilters, setDebouncedTableFilters] = useState(filters);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedTableFilters(filters), 400);
    return () => clearTimeout(timer);
  }, [filters]);

  const buildTableQuery = useCallback(
    (pageIndex, limit, filterState, exportAll = false) => {
      const validStart = getValidDate(startDate);
      const validEnd = getValidDate(endDate);
      let startStr = formatLocalDate(validStart);
      let endStr = formatLocalDate(validEnd);
      if (startStr > endStr) {
        [startStr, endStr] = [endStr, startStr];
      }
      const params = new URLSearchParams({
        app_id: selectedApp,
        start_date: startStr,
        end_date: endStr,
        page: String(pageIndex + 1),
        limit: String(limit),
        sort_key: sortConfig.key,
        sort_dir: sortConfig.direction,
      });
      if (productFilter && productFilter !== 'all') {
        params.append('product_sku', productFilter);
      }
      if (exportAll) {
        params.set('export', '1');
      }
      Object.entries(filterState).forEach(([key, value]) => {
        if (value != null && String(value).trim() !== '') {
          params.set(key, String(value));
        }
      });
      return params.toString();
    },
    [selectedApp, startDate, endDate, productFilter, sortConfig]
  );

  const loadTablePage = useCallback(async (exportAll = false) => {
    const baseUrl = (process.env.REACT_APP_API_BASE_URL || '').replace(/\/$/, '');
    if (!baseUrl || !selectedApp) return null;

    const requestId = ++tableRequestId.current;
    if (!exportAll) {
      setTableLoading(true);
    }

    try {
      const query = buildTableQuery(page, rowsPerPage, debouncedTableFilters, exportAll);
      const res = await fetch(`${baseUrl}/subscriptions-table?${query}`);
      if (!res.ok) {
        throw new Error(`Failed to load subscriptions table: ${res.status}`);
      }
      const data = await res.json();
      if (requestId !== tableRequestId.current) return null;

      if (!exportAll) {
        setTableSubs(Array.isArray(data.subscriptions) ? data.subscriptions : []);
        setTableTotal(typeof data.total === 'number' ? data.total : 0);
      }
      return data;
    } catch (error) {
      console.error('Error fetching subscriptions table:', error);
      return null;
    } finally {
      if (requestId === tableRequestId.current && !exportAll) {
        setTableLoading(false);
      }
    }
  }, [buildTableQuery, debouncedTableFilters, page, rowsPerPage, selectedApp]);

  useEffect(() => {
    if (!selectedApp || tabValue !== 2) return;
    loadTablePage();
  }, [selectedApp, startDate, endDate, productFilter, page, rowsPerPage, debouncedTableFilters, sortConfig, tabValue, loadTablePage]);

  useEffect(() => {
    setPage(0);
  }, [selectedApp, startDate, endDate, productFilter]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev.key === key && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(0);
  };

  const handleSearchChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(0); // reset to first page
  };

  const paginatedSubs = tableSubs;

  // Revenue Over Time total
  const totalRevenue = chartDailyRevenue.reduce((sum, d) => sum + d.revenue, 0);
  // Subscription Growth totals
  const totalNewSubs = dailySubsData.reduce((sum, d) => sum + d.newCount, 0);
  const totalRenewals = dailySubsData.reduce((sum, d) => sum + d.renewalCount, 0);
  const totalCancellations = dailySubsData.reduce((sum, d) => sum + d.todayCancellations, 0);
  const totalExpired = dailySubsData.reduce((sum, d) => sum + (d.expiredCount || 0), 0);
  // Installs Growth totals
  const totalInstalls = dailyInstalls ? dailyInstalls.reduce((sum, d) => sum + (d.first_open || 0), 0) : 0;
  const totalUninstalls = dailyInstalls ? dailyInstalls.reduce((sum, d) => sum + (d.app_remove || 0), 0) : 0;

  const exportToExcel = (data, filename) => {
    const worksheet = XLSX.utils.json_to_sheet(data);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Data");
    XLSX.writeFile(workbook, `${filename}.xlsx`);
  };

  const exportTransactions = async () => {
    const data = await loadTablePage(true);
    const rows = data?.subscriptions || [];
    const exportData = rows.map((sub) => ({
      Date: format(new Date(sub.purchaseDate), 'yyyy-MM-dd HH:mm:ss'),
      Product: sub.productSku,
      Revenue: sub.convertedPrice?.toFixed(2),
      Country: sub.country,
      Status: sub.isSubscriptionActive ? 'Active' : 'Inactive',
      Platform: sub.platform,
      Type: sub.id === sub.originalPurchase ? 'New' : 'Renewal',
    }));
    exportToExcel(exportData, `transactions_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const exportDailyData = () => {
    const exportData = chartDailyRevenue.map(day => ({
      Date: day.date,
      Revenue: day.revenue.toFixed(2)
    }));
    exportToExcel(exportData, `daily_revenue_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const exportSubscriptionGrowth = () => {
    const exportData = dailySubsData.map(day => {
      const row = {
        Date: day.date,
        'New Subscriptions': day.newCount,
        Renewals: day.renewalCount,
        Cancellations: day.todayCancellations,
        Expired: day.expiredCount || 0,
      };
      return row;
    });
    exportToExcel(exportData, `subscription_growth_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  const exportInstallsData = () => {
    if (!dailyInstalls) return;
    const exportData = dailyInstalls.map(day => ({
      Date: day.date,
      Installs: day.first_open || 0,
      Uninstalls: day.app_remove || 0
    }));
    exportToExcel(exportData, `installs_data_${format(new Date(), 'yyyy-MM-dd')}`);
  };

  return (
    <>
    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
      {/* Thin refresh bar when updating cached data */}
      {refreshing && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1300 }} />
      )}
      {/* Main Content */}
      {loading ? (
        <PageLoader/>
      ) : (
          <Box>
            {/* Filters */}
            <Paper sx={{ p: 2, mb: 3, pt: 7, width: '100%' }} justifyContent={"center"}>
              
              <Grid container spacing={1} alignItems="center" justifyContent={"center"}>
                <Typography variant="h6">
                  <FilterList fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                  Filters
                </Typography>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Date Range</InputLabel>
                    <Select
                      value={dateRange}
                      label="Date Range"
                      onChange={(e) => handleDateRangeChange(e.target.value)}
                    >
                      <MenuItem value="last24">Last 24 Hours</MenuItem>
                      <MenuItem value="7days">Last 7 Days</MenuItem>
                      <MenuItem value="30days">Last 30 Days</MenuItem>
                      <MenuItem value="90days">Last 90 Days</MenuItem>
                      <MenuItem value="thisMonth">This Month</MenuItem>
                      <MenuItem value="lastMonth">Last Month</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Start Date"
                      value={startDate}
                      onChange={(date) => setStartDate(date)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="End Date"
                      value={endDate}
                      onChange={(date) => setEndDate(date)}
                      slotProps={{ textField: { size: 'small', fullWidth: true } }}
                    />
                  </LocalizationProvider>
                </Grid>
                <Grid item xs={12} md={3}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product</InputLabel>
                    <Select
                      value={productFilter}
                      label="Product"
                      onChange={(e) => setProductFilter(e.target.value)}
                    >
                      <MenuItem value="all">All Products</MenuItem>
                      {productTypes.map((type, index) => (
                        <MenuItem key={index} value={type}>{type}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              </Grid>
            </Paper>

            {/* Key Metrics Cards */}
            <Grid container spacing={3} justifyContent={"center"}>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Today's Revenue</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CreditCard sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="h4" color="green">${todayRevenue.toFixed(2)}</Typography>
                    </Box>
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      {todaySubs} Subscriptions Today
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Today's subscriptions</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="h5" color="green">{newTodaySubs} New</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h5" color="primary">{renewalTodaySubs} Renewals</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: '#d32f2f', mr: 1 }} />
                      <Typography variant="h5" color="error">{todayCancellations} Cancellations</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: '#ff9800', mr: 1 }} />
                      <Typography variant="h5" sx={{ color: '#ff9800' }}>{todayExpired} Expired</Typography>
                    </Box>
                    {todayRefunds > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccountCircle sx={{ color: '#f44336', mr: 1 }} />
                        <Typography variant="h6" sx={{ color: '#f44336' }}>{todayRefunds} Refunds</Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Today's installs</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'green', mr: 1 }} />
                      <Typography variant="h5" color="green">{todayInstalls} Installs</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: '#d32f2f', mr: 1 }} />
                      <Typography variant="h5" color="error">{todayUninstalls} Uninstalls</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Active Subscriptions</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <AccountCircle sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h4" color="primary">{activeSubs}</Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>
                      Filtered Period Revenue
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <TrendingUp sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h4" color="primary">${filteredPeriodRevenue.toFixed(2)}</Typography>
                    </Box>
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      {filteredTransactionCount} Transactions
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              {/*<Grid item xs={12} md={3}>
                <Card sx={{ height: '100%', backgroundColor: '#f9f9f9' }}>
                  <CardContent>
                    <Typography color="textSecondary" gutterBottom>Monthly Recurring Revenue</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <CalendarToday sx={{ color: 'primary.main', mr: 1 }} />
                      <Typography variant="h4" color="primary">${mrr.toFixed(2)}</Typography>
                    </Box>
                    <Typography color="textSecondary" sx={{ mt: 1 }}>
                      All-Time: ${totalRevenue.toFixed(2)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>*/}
            </Grid>

            {/* Tabs for different chart views */}
            <Paper sx={{ mt: 3 }}>
              <Tabs 
                value={tabValue} 
                onChange={handleTabChange} 
                variant="scrollable"
                scrollButtons="auto"
                aria-label="dashboard tabs"
              >
                <Tab icon={<ShowChart />} label="Revenue Trends" />
                <Tab icon={<PieChartIcon />} label="Revenue by Product" />
                <Tab icon={<Assessment />} label="Transaction Details" />
                <Tab icon={<CompareArrows />} label="Compare Days" />
              </Tabs>
            </Paper>

            {/* Tab Content */}
            <Box sx={{ mt: 3 }} justifyContent={"center"}>
              {/* Revenue Trends Tab */}
              {tabValue === 0 && (
                <Grid container spacing={3} justifyContent={"center"}>
                  <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Card sx={{width: '100%'}}>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Revenue Over Time</Typography>
                          <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={exportDailyData}
                            size="small"
                          >
                            Export Data
                          </Button>
                        </Box>
                        <Typography variant="subtitle1" color="primary" sx={{ mb: 1 }}>
                          Total: ${totalRevenue.toFixed(2)}
                        </Typography>
                        <ResponsiveContainer width="100%" height={300}>
                          <LineChart data={chartDailyRevenue} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={45} />
                            <YAxis />
                            <Tooltip formatter={(value) => [`$${value.toFixed(2)}`, 'Revenue']} />
                            <Line 
                              type="monotone" 
                              dataKey="revenue" 
                              stroke="#3f51b5" 
                              name="Revenue" 
                              legendType='none'
                              strokeWidth={2} 
                              dot={{ r: 3 }}
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Subscription Growth</Typography>
                          <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={exportSubscriptionGrowth}
                            size="small"
                          >
                            Export Data
                          </Button>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 1, flexWrap: 'wrap', gap: 1 }}>
                          <Typography color="success.main">New: {totalNewSubs}</Typography>
                          <Typography color="primary.main">Renewals: {totalRenewals}</Typography>
                          <Typography color="error.main">Cancellations: {totalCancellations}</Typography>
                          <Typography sx={{ color: '#ff9800' }}>Expired: {totalExpired}</Typography>
                        </Stack>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dailySubsData} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={75} />
                            <YAxis />
                            <Tooltip 
                              formatter={(value, name) => {
                                const labelMap = {
                                  newCount: 'New Subscriptions',
                                  renewalCount: 'Renewals',
                                  todayCancellations: 'Cancellations',
                                  expiredCount: 'Expired',
                                };
                                return [value, labelMap[name] || name];
                              }} 
                            />
                            <Bar dataKey="newCount" name="New Subscriptions" stackId="a" fill="#82ca9d" />
                            <Bar dataKey="renewalCount" name="Renewals" stackId="a" fill="#3f51b5" />
                            <Bar dataKey="todayCancellations" name="Cancellations" stackId="b" fill="#d32f2f" />
                            <Bar dataKey="expiredCount" name="Expired" stackId="b" fill="#ff9800" />
                            </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={12} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                          <Typography variant="h6">Installs Growth</Typography>
                          <Button
                            variant="contained"
                            startIcon={<FileDownload />}
                            onClick={exportInstallsData}
                            size="small"
                          >
                            Export Data
                          </Button>
                        </Box>
                        <Stack direction="row" spacing={2} sx={{ mb: 1 }}>
                          <Typography color="success.main">Installs: {totalInstalls}</Typography>
                          <Typography color="error.main">Uninstalls: {totalUninstalls}</Typography>
                        </Stack>
                        <ResponsiveContainer width="100%" height={300}>
                          <BarChart data={dailyInstalls} margin={{ top: 10, right: 30, left: 20, bottom: 30 }}>
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="date" angle={-45} textAnchor="end" height={75} />
                            <YAxis />
                            <Tooltip 
                              formatter={(value, name) => {
                                const labelMap = {
                                  first_open: 'Installs',
                                  app_remove: 'Uninstalls'
                                };
                                return [value, labelMap[name] || name];
                              }} 
                            />
                            <Bar dataKey="first_open" name="Installs" stackId="a" fill="#82ca9d" />
                            <Bar dataKey="app_remove" name="Uninstalls" stackId="a" fill="#d32f2f" />
                            </BarChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              )}

              {/* Revenue by Product Tab */}
              {tabValue === 1 && (
                <Grid container spacing={3} justifyContent={"center"}>
                  <Grid item xs={12} md={6} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6">Revenue by Product</Typography>
                        <ResponsiveContainer width="100%" height={350}>
                          <PieChart>
                            <Pie
                              data={productData.slice(0, 5)}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="value"
                              nameKey="name"
                              label={(entry) => {
                                const dataEntry = productData.slice(0, 5).find(item => item.name === entry.name);
                                return dataEntry ? `${dataEntry.revenuePercent.toFixed(1)}%` : '';
                              }}
                            >
                              {productData.slice(0, 5).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value, name, props) => [props.payload.count, 'Count']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{width: '100%'}}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>Subscription Count by Product</Typography>
                        <ResponsiveContainer width="100%" height={350}>
                          <PieChart>
                            <Pie
                              data={productData.slice(0, 5)}
                              cx="50%"
                              cy="50%"
                              labelLine={true}
                              outerRadius={100}
                              fill="#8884d8"
                              dataKey="count"
                              nameKey="name"
                              label={(entry) => {
                                const dataEntry = productData.slice(0, 5).find(item => item.name === entry.name);
                                return dataEntry ? `${dataEntry.countPercent.toFixed(1)}%` : '';
                              }}
                            >
                              {productData.slice(0, 5).map((entry, index) => (
                                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip formatter={(value) => [value, 'Count']} />
                            <Legend />
                          </PieChart>
                        </ResponsiveContainer>
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12} md={6} sx={{width: '100%'}}>
                    <TableContainer component={Paper}>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>Product</TableCell>
                            <TableCell align="right">Subscriptions</TableCell>
                            <TableCell align="right">Revenue</TableCell>
                            <TableCell align="right">Avg. Price</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell component="th" scope="row">{row.name}</TableCell>
                              <TableCell align="right">{row.count}</TableCell>
                              <TableCell align="right">${row.value.toFixed(2)}</TableCell>
                              <TableCell align="right">${(row.value / row.count).toFixed(2)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                </Grid>
              )}

              {/* Transaction Details Tab */}
              {tabValue === 2 && (
                <Grid container spacing={4} justifyContent="center">
               {/* Comparison and Chart */}
               <Grid item xs={12} md={8} width="100%">
                <Paper>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 2 }}>
                    <Button
                      variant="contained"
                      startIcon={<FileDownload />}
                      onClick={exportTransactions}
                      size="small"
                    >
                      Export Transactions
                    </Button>
                  </Box>
                  <TableContainer component={Paper}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Date"
                              value={filters.purchaseDate}
                              onChange={(e) => handleSearchChange('purchaseDate', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Product"
                              value={filters.productSku}
                              onChange={(e) => handleSearchChange('productSku', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Revenue"
                              value={filters.convertedPrice}
                              onChange={(e) => handleSearchChange('convertedPrice', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              variant="standard"
                              placeholder="Search Country"
                              value={filters.country}
                              onChange={(e) => handleSearchChange('country', e.target.value)}
                            />
                          </TableCell>
                          <TableCell>
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Status</InputLabel>
                              <Select
                                value={filters.status}
                                onChange={(e) => handleSearchChange('status', e.target.value)}
                                label="Status"
                              >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="Active">Active</MenuItem>
                                <MenuItem value="Inactive">Inactive</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Platform</InputLabel>
                              <Select
                                value={filters.platform}
                                onChange={(e) => handleSearchChange('platform', e.target.value)}
                                label="Platform"
                              >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="ios">iOS</MenuItem>
                                <MenuItem value="android">Android</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                          <TableCell>
                            <FormControl variant="standard" fullWidth>
                              <InputLabel>Type</InputLabel>
                              <Select
                                value={filters.type}
                                onChange={(e) => handleSearchChange('type', e.target.value)}
                                label="Type"
                              >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="New">New</MenuItem>
                                <MenuItem value="Renewal">Renewal</MenuItem>
                              </Select>
                            </FormControl>
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell onClick={() => handleSort('purchaseDate')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            Date {sortConfig.key === 'purchaseDate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell onClick={() => handleSort('productSku')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            Product {sortConfig.key === 'productSku' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell onClick={() => handleSort('convertedPrice')} sx={{ cursor: 'pointer', fontWeight: 'bold' }}>
                            Revenue {sortConfig.key === 'convertedPrice' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Country</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Platform</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Type</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {paginatedSubs.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} align="center" sx={{ py: 6, color: "text.secondary" }}>
                              No transactions found.
                            </TableCell>
                          </TableRow>
                        ) : (
                        paginatedSubs.map((sub) => (
                          <TableRow key={sub.id}>
                            <TableCell>{format(new Date(sub.purchaseDate), 'yyyy-MM-dd HH:mm:ss')}</TableCell>
                            <TableCell>{sub.productSku}</TableCell>
                            <TableCell>{'$'}{sub.convertedPrice?.toFixed(2)}</TableCell>
                            <TableCell>{sub.country}</TableCell><TableCell>
                              {sub.isSubscriptionActive
                                ? <span style={{ color: 'green' }}>Active</span>
                                : <span style={{ color: 'red' }}>Inactive</span>
                              }
                            </TableCell>
                            <TableCell>{sub.platform}</TableCell>
                            <TableCell>{sub.id === sub.originalPurchase ? 'New' : 'Renewal'}</TableCell>
                          </TableRow>
                        )))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {tableLoading && <LinearProgress />}
                  <TablePagination
                    component="div"
                    count={tableTotal}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                    sx={{ width: "100%" }}
                  />
                </Paper>
                </Grid>
                </Grid>
              )}
              {/* Compare 2 days Tab */}
              {tabValue === 3 && (
               <Grid container spacing={4} justifyContent="center">
               {/* Comparison and Chart */}
               <Grid item xs={12} md={8} width="100%">
                 <Card sx={{ p: 2 }}>
                   <CardContent>
                     <LocalizationProvider dateAdapter={AdapterDateFns}>
                       <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
                         <DatePicker label="Select Day 1" value={day1} onChange={setDay1} />
                         <DatePicker label="Select Day 2" value={day2} onChange={setDay2} />
                       </Stack>
                     </LocalizationProvider>
                     {loadingCharts ? (
                       <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300 }}>
                         <CircularProgress />
                       </Box>
                     ) : (
                       <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={2} alignItems="center">
                         <ResponsiveContainer width="100%" height={300}>
                           <BarChart data={[
                             {
                               name: 'Subscriptions',
                               [`New ${formatLocalDate(day1)}`]: day1Metrics.new_subscriptions ?? day1Metrics.subscriptions,
                               [`Renewal ${formatLocalDate(day1)}`]: day1Metrics.renewal_subscriptions ?? 0,
                               [`Cancellations ${formatLocalDate(day1)}`]: day1Metrics.cancellations ?? 0,
                               [`New ${formatLocalDate(day2)}`]: day2Metrics.new_subscriptions ?? day2Metrics.subscriptions,
                               [`Renewal ${formatLocalDate(day2)}`]: day2Metrics.renewal_subscriptions ?? 0,
                               [`Cancellations ${formatLocalDate(day2)}`]: day2Metrics.cancellations ?? 0,
                             }
                           ]}>
                             <XAxis dataKey="name" />
                             <YAxis />
                             <Tooltip />
                             <Bar dataKey={`New ${formatLocalDate(day1)}`} fill="#82ca9d" name={`New ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Renewal ${formatLocalDate(day1)}`} fill="#3f51b0" name={`Renewal ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Cancellations ${formatLocalDate(day1)}`} fill="#d32f2f" name={`Cancellations ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`New ${formatLocalDate(day2)}`} fill="#82ca9d" name={`New ${formatLocalDate(day2)}`}/>
                             <Bar dataKey={`Renewal ${formatLocalDate(day2)}`} fill="#3f51b5" name={`Renewal ${formatLocalDate(day2)}`}/>
                             <Bar dataKey={`Cancellations ${formatLocalDate(day2)}`} fill="#d32f2f" name={`Cancellations ${formatLocalDate(day2)}`}/>
                           </BarChart>
                         </ResponsiveContainer>
                         <ResponsiveContainer width="100%" height={300}>
                           <BarChart data={[
                             {
                               name: 'Revenue',
                               [formatLocalDate(day1)]: day1Metrics.revenue ? day1Metrics.revenue.toFixed(2) : 0,
                               [formatLocalDate(day2)]: day2Metrics.revenue ? day2Metrics.revenue.toFixed(2) : 0,
                             }
                           ]}>
                             <XAxis dataKey="name" />
                             <YAxis />
                             <Tooltip />
                             <Bar dataKey={formatLocalDate(day1)} fill="#8884d8" />
                             <Bar dataKey={formatLocalDate(day2)} fill="#82ca9d" />
                           </BarChart>
                         </ResponsiveContainer>
                         {/* Installs & Uninstalls Comparison (merged) */}
                         <ResponsiveContainer width="100%" height={300}>
                           <BarChart data={[
                             {
                               name: 'Installs/Uninstalls',
                               [`Installs ${formatLocalDate(day1)}`]: day1Metrics.installs || 0,
                               [`Uninstalls ${formatLocalDate(day1)}`]: day1Metrics.uninstalls || 0,
                               [`Installs ${formatLocalDate(day2)}`]: day2Metrics.installs || 0,
                               [`Uninstalls ${formatLocalDate(day2)}`]: day2Metrics.uninstalls || 0,
                             }
                           ]}>
                             <XAxis dataKey="name" />
                             <YAxis />
                             <Tooltip />
                             <Bar dataKey={`Installs ${formatLocalDate(day1)}`} fill="#82ca9d" name={`Installs ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Uninstalls ${formatLocalDate(day1)}`} fill="#d32f2f" name={`Uninstalls ${formatLocalDate(day1)}`}/>
                             <Bar dataKey={`Installs ${formatLocalDate(day2)}`} fill="#82ca9d" name={`Installs ${formatLocalDate(day2)}`}/>
                             <Bar dataKey={`Uninstalls ${formatLocalDate(day2)}`} fill="#d32f2f" name={`Uninstalls ${formatLocalDate(day2)}`}/>
                           </BarChart>
                         </ResponsiveContainer>
                       </Stack>
                     )}
                   </CardContent>
                 </Card>
               </Grid>
               {/* Table */}
               <Grid item xs={12} width="100%">
                 <Card>
                   <CardContent>
                     <Typography variant="h6" gutterBottom>
                       Subscription Details
                     </Typography>
                     <TableContainer component={Paper}>
                       <Table>
                         <TableHead>
                           <TableRow>
                             <TableCell>Date</TableCell>
                             <TableCell align="right">Subscriptions</TableCell>
                             <TableCell align="right">New Subs</TableCell>
                             <TableCell align="right">Renewals</TableCell>
                             <TableCell align="right">Revenue</TableCell>
                             <TableCell align="right">Installs</TableCell>
                             <TableCell align="right">Uninstalls</TableCell>
                             <TableCell align="right">Cancellations</TableCell>
                             <TableCell align="right">Expired</TableCell>
                             <TableCell align="right">Retry</TableCell>
                             <TableCell align="right">Refunds</TableCell>
                           </TableRow>
                         </TableHead>
                         <TableBody>
                           {[{ day: day1, metrics: day1Metrics }, { day: day2, metrics: day2Metrics }].map((entry, index) => (
                             <TableRow key={index}>
                               <TableCell>{formatLocalDate(entry.day)}</TableCell>
                               <TableCell align="right">{entry.metrics.subscriptions}</TableCell>
                               <TableCell align="right">{entry.metrics.new_subscriptions ?? entry.metrics.subscriptions}</TableCell>
                               <TableCell align="right">{entry.metrics.renewal_subscriptions ?? 0}</TableCell>
                               <TableCell align="right">${entry.metrics.revenue?.toFixed(2)}</TableCell>
                               <TableCell align="right">{entry.metrics.installs || 0}</TableCell>
                               <TableCell align="right">{entry.metrics.uninstalls || 0}</TableCell>
                               <TableCell align="right">{entry.metrics.cancellations ?? 0}</TableCell>
                               <TableCell align="right">{entry.metrics.expired ?? 0}</TableCell>
                               <TableCell align="right">{entry.metrics.renewal_retry ?? 0}</TableCell>
                               <TableCell align="right">{entry.metrics.refunds ?? 0}</TableCell>
                             </TableRow>
                           ))}
                         </TableBody>
                       </Table>
                     </TableContainer>
                   </CardContent>
                 </Card>
               </Grid>
             </Grid>
            )}
          </Box>
        </Box>) }
      </Box>
  </>
  );
};

export default SubscriptionDashboard;